import React, { useEffect, useRef, useCallback } from "react";
import { message } from "antd";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { getBrandedLoginPath } from "@/pages/signup/brandContext";

interface SessionMonitorProps {
  children: React.ReactNode;
  checkInterval?: number; // check interval, default 30 seconds
  enableStorageListener?: boolean; // whether to enable storage listener
  enableVisibilityListener?: boolean; // whether to enable page visibility listener
}

const SessionMonitor: React.FC<SessionMonitorProps> = ({
  children,
  checkInterval = 30000,
  enableStorageListener = true,
  enableVisibilityListener = true,
}) => {
  const { user, detectUserSwitch, forceLogout, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const isCheckingRef = useRef<boolean>(false);

  // session validation function
  const validateSession = useCallback(async () => {
    if (!user || !isAuthenticated || isCheckingRef.current) {
      return;
    }

    isCheckingRef.current = true;

    try {
      // check user switch
      const hasUserSwitch = await detectUserSwitch();
      if (hasUserSwitch) {
        console.warn("SessionMonitor: User switch detected");
        message.warning("detected user switch, cleaning old session");
        await forceLogout();
        navigate(getBrandedLoginPath());
        return;
      }

      // can add other session validation logic here
      // e.g. check token validity, server-side validation, etc.
    } catch (error) {
      console.error("SessionMonitor: Session validation failed", error);
      message.error("session validation failed, please login again");
      await forceLogout();
      navigate(getBrandedLoginPath());
    } finally {
      isCheckingRef.current = false;
    }
  }, [user, isAuthenticated, detectUserSwitch, forceLogout, navigate]);

  // start periodic check
  const startPeriodicCheck = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    if (user && isAuthenticated) {
      intervalRef.current = setInterval(validateSession, checkInterval);
      // console.log(`SessionMonitor: Started periodic check every ${checkInterval}ms`);
    }
  }, [user, isAuthenticated, validateSession, checkInterval]);

  // stop periodic check
  const stopPeriodicCheck = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
      // console.log('SessionMonitor: Stopped periodic check');
    }
  }, []);

  // handle storage change
  const handleStorageChange = useCallback(
    (e: StorageEvent) => {
      if (!enableStorageListener) return;

      // check critical storage changes
      if (
        e.key?.includes("user") ||
        e.key?.includes("access_token") ||
        e.key?.startsWith("CognitoIdentityServiceProvider")
      ) {
        // console.log('SessionMonitor: Storage change detected:', e.key);

        // delay validation, avoid frequent triggering
        setTimeout(() => {
          validateSession();
        }, 500);
      }
    },
    [enableStorageListener, validateSession],
  );

  // handle page visibility change
  const handleVisibilityChange = useCallback(() => {
    if (!enableVisibilityListener) return;

    if (!document.hidden && user && isAuthenticated) {
      // console.log('SessionMonitor: Page became visible, validating session');
      validateSession();
    }
  }, [enableVisibilityListener, user, isAuthenticated, validateSession]);

  // handle page focus change
  const handleFocusChange = useCallback(() => {
    if (!enableVisibilityListener) return;

    if (user && isAuthenticated) {
      // console.log('SessionMonitor: Page focused, validating session');
      validateSession();
    }
  }, [enableVisibilityListener, user, isAuthenticated, validateSession]);

  useEffect(() => {
    // start or stop periodic check
    if (user && isAuthenticated) {
      startPeriodicCheck();
    } else {
      stopPeriodicCheck();
    }

    return () => {
      stopPeriodicCheck();
    };
  }, [user, isAuthenticated, startPeriodicCheck, stopPeriodicCheck]);

  useEffect(() => {
    // add event listeners
    if (enableStorageListener) {
      window.addEventListener("storage", handleStorageChange);
    }

    if (enableVisibilityListener) {
      document.addEventListener("visibilitychange", handleVisibilityChange);
      window.addEventListener("focus", handleFocusChange);
    }

    return () => {
      // clean event listeners
      if (enableStorageListener) {
        window.removeEventListener("storage", handleStorageChange);
      }

      if (enableVisibilityListener) {
        document.removeEventListener(
          "visibilitychange",
          handleVisibilityChange,
        );
        window.removeEventListener("focus", handleFocusChange);
      }
    };
  }, [
    enableStorageListener,
    enableVisibilityListener,
    handleStorageChange,
    handleVisibilityChange,
    handleFocusChange,
  ]);

  // clean up when component unmounts
  useEffect(() => {
    return () => {
      stopPeriodicCheck();
    };
  }, [stopPeriodicCheck]);

  return <>{children}</>;
};

export default SessionMonitor;
